import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import api from '../utils/api';

import PartnerLoginForm from './components/PartnerLoginForm';

const LoginComponent = () => {
  const navigate = useNavigate();

  // ═══════════════════════════════════════════════
  // AUTHENTICATION STATES (EXACT LOGIC PRESERVED)
  // ═══════════════════════════════════════════════
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [category, setCategory] = useState("Restaurant");

  // OTP Logic States
  const [isOtpSelectionView, setIsOtpSelectionView] = useState(true);
  const [isEmailOtpView, setIsEmailOtpView] = useState(false);
  const [isSmsOtpView, setIsSmsOtpView] = useState(false);
  const [email, setEmail] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [smsOtpSent, setSmsOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [smsOtp, setSmsOtp] = useState(['', '', '', '', '', '']);
  const [otpIdentifier, setOtpIdentifier] = useState('');

  // Forgot Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordMethod, setForgotPasswordMethod] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);

  // ═══════════════════════════════════════════════
  // LANDING PAGE UI INTERACTIVE STATES
  // ═══════════════════════════════════════════════
  const [isScrolled, setIsScrolled] = useState(false);
  const [problemFilter, setProblemFilter] = useState('both');
  const [activeBenefitIdx, setActiveBenefitIdx] = useState(0);
  const [openFaqIdx, setOpenFaqIdx] = useState(0);
  const [activeNavSection, setActiveNavSection] = useState('');
  const [isSwitchingBenefit, setIsSwitchingBenefit] = useState(false);
  const [hoveredBladeIdx, setHoveredBladeIdx] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['problem', 'how', 'benefits', 'categories', 'faq'];
      let current = '';
      sections.forEach(secId => {
        const el = document.getElementById(secId);
        if (el) {
          const top = el.offsetTop - 120;
          const height = el.offsetHeight;
          if (window.scrollY >= top && window.scrollY < top + height) {
            current = secId;
          }
        }
      });
      setActiveNavSection(current);
    };

    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // ═══════════════════════════════════════════════
  // AUTHENTICATION HANDLERS (STRICT COMPLIANCE)
  // ═══════════════════════════════════════════════
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone || !password) { toast.error('Fields required'); return; }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/login', {
        phone_number: phone,
        password,
        role: category,
      });

      const data = response.data;
      if (data.message !== "Invalid phone number or password") {
        if (category === "Restaurant") {
          localStorage.setItem("token_partner_restaurant", data.token);
          localStorage.setItem("id_partner_restaurant", data.user._id);
          toast.success('Accessing Restaurant Portal...');
          setTimeout(() => navigate("/restaurant"), 500);
        } else if (category === "Shop") {
          localStorage.setItem("token_partner_shop", data.token);
          localStorage.setItem("id_partner_shop", data.user._id);
          toast.success('Accessing Shop Portal...');
          setTimeout(() => navigate("/shop"), 500);
        } else if (category === "Activities" || category === "Activity") {
          localStorage.setItem("token_partner_activities", data.token);
          localStorage.setItem("id_partner_activities", data.user._id);
          toast.success('Accessing Activities Portal...');
          setTimeout(() => navigate("/activity"), 500);
        }
      } else {
        toast.error("Invalid phone number or password");
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response && error.response.status === 403) {
        toast.error("Account not approved yet. Please wait for admin approval.");
      } else {
        toast.error('Login failed. Please check credentials.');
      }
    } finally { setIsLoading(false); }
  };

  const handleSendOtp = async () => {
    if (!otpIdentifier) {
      toast.error('Please enter email or phone number');
      return;
    }

    setIsLoading(true);
    try {
      const isEmail = otpIdentifier.includes('@');
      if (isEmail) {
        setEmail(otpIdentifier);
        const response = await api.post('/auth/sendOtp', { email: otpIdentifier, role: category });
        if (response.data.message === 'OTP sent to your email') {
          setOtpSent(true);
          setIsEmailOtpView(true);
          setIsOtpSelectionView(false);
          toast.success('OTP sent to your email!');
        }
      } else {
        const cleanPhone = otpIdentifier.replace(/\D/g, '');
        if (cleanPhone.length !== 10) {
          toast.error('Please enter a valid 10-digit phone number');
          setIsLoading(false);
          return;
        }
        setSmsPhone(cleanPhone);
        const response = await api.post('/auth/sendSmsOtp', { phone_number: cleanPhone, role: category });
        if (response.data.message === 'OTP sent successfully') {
          setSmsOtpSent(true);
          setIsSmsOtpView(true);
          setIsOtpSelectionView(false);
          toast.success('OTP sent to your phone!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async () => {
    if (!email) { toast.error('Please enter your email'); return; }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/sendOtp', { email, role: category });
      if (response.data.message === 'OTP sent to your email') {
        setOtpSent(true);
        toast.success('OTP sent to your email!');
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to send OTP'); } finally { setIsLoading(false); }
  };

  const handleSmsLogin = async () => {
    if (!smsPhone || smsPhone.length !== 10) { toast.error('Enter valid 10-digit number'); return; }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/sendSmsOtp', { phone_number: smsPhone, role: category });
      if (response.data.message === 'OTP sent successfully') {
        setSmsOtpSent(true);
        toast.success('OTP sent to your phone!');
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Failed to send OTP'); } finally { setIsLoading(false); }
  };

  const handleVerifyEmailOtp = async () => {
    const otpString = emailOtp.join('');
    if (otpString.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/verifyOtp', { email, otp: otpString });
      if (isForgotPassword) {
        setShowNewPasswordForm(true);
        toast.success("OTP verified!");
      } else if (response.data.message === 'Login Successful') {
        const data = response.data;
        if (data.user.role === "Restaurant") {
          localStorage.setItem("token_partner_restaurant", data.token);
          localStorage.setItem("id_partner_restaurant", data.user._id);
          navigate("/restaurant");
        } else if (data.user.role === "Shop") {
          localStorage.setItem("token_partner_shop", data.token);
          localStorage.setItem("id_partner_shop", data.user._id);
          navigate("/shop");
        } else if (data.user.role === "Activity" || data.user.role === "Activities") {
          localStorage.setItem("token_partner_activities", data.token);
          localStorage.setItem("id_partner_activities", data.user._id);
          navigate("/activity");
        }
        toast.success('Login successful!');
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Verification failed'); } finally { setIsLoading(false); }
  };

  const handleVerifySmsOtp = async () => {
    const otpString = smsOtp.join('');
    if (otpString.length !== 6) { toast.error('Enter 6-digit OTP'); return; }
    setIsLoading(true);
    try {
      const response = await api.post('/auth/verifySmsOtp', { phone_number: smsPhone, otp: otpString });
      if (isForgotPassword) {
        setShowNewPasswordForm(true);
        toast.success("OTP verified!");
      } else if (response.data.message === 'Login successful') {
        const data = response.data;
        if (data.user.role === "Restaurant") {
          localStorage.setItem("token_partner_restaurant", data.token);
          localStorage.setItem("id_partner_restaurant", data.user._id);
          navigate("/restaurant");
        } else if (data.user.role === "Shop") {
          localStorage.setItem("token_partner_shop", data.token);
          localStorage.setItem("id_partner_shop", data.user._id);
          navigate("/shop");
        } else if (data.user.role === "Activity" || data.user.role === "Activities") {
          localStorage.setItem("token_partner_activities", data.token);
          localStorage.setItem("id_partner_activities", data.user._id);
          navigate("/activity");
        }
        toast.success('Login successful!');
      }
    } catch (error) { toast.error(error.response?.data?.message || 'Verification failed'); } finally { setIsLoading(false); }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (!newPassword) { toast.error('Password cannot be empty'); return; }
    setIsLoading(true);
    try {
      const payload = { newPassword, ...(forgotPasswordMethod === 'email' ? { email } : { phone_number: smsPhone }) };
      await api.post('/auth/reset-password', payload);
      toast.success('Password reset successful!');
      resetToLogin();
    } catch (error) { toast.error(error.response?.data?.message || 'Reset failed'); } finally { setIsLoading(false); }
  };

  const resetToLogin = () => {
    setIsOtpSelectionView(true);
    setIsEmailOtpView(false);
    setIsSmsOtpView(false);
    setIsForgotPassword(false);
    setOtpSent(false);
    setSmsOtpSent(false);
    setShowNewPasswordForm(false);
    setForgotPasswordMethod('');
    setNewPassword('');
    setConfirmPassword('');
    setEmail('');
    setSmsPhone('');
    setOtpIdentifier('');
    setEmailOtp(['', '', '', '', '', '']);
    setSmsOtp(['', '', '', '', '', '']);
  };

  const handleEmailOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...emailOtp];
      newOtp[index] = value;
      setEmailOtp(newOtp);
      if (value !== '' && index < 5) {
        document.querySelector(`input[name='email-otp-${index + 1}']`)?.focus();
      }
    }
  };

  const handleSmsOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...smsOtp];
      newOtp[index] = value;
      setSmsOtp(newOtp);
      if (value !== '' && index < 5) {
        document.querySelector(`input[name='sms-otp-${index + 1}']`)?.focus();
      }
    }
  };

  const handleSwitchBenefit = (idx) => {
    if (isSwitchingBenefit) return;
    setIsSwitchingBenefit(true);
    setActiveBenefitIdx(idx);
    setTimeout(() => setIsSwitchingBenefit(false), 180);
  };

  const scrollToSection = (e, id) => {
    if (e) e.preventDefault();
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const topOffset = element.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({
        top: topOffset,
        behavior: 'smooth'
      });
    }
  };

  const isDesktop = windowWidth >= 768;

  const authStates = {
    phone, setPhone,
    password, setPassword,
    isLoading,
    showPassword, setShowPassword,
    category, setCategory,
    isOtpSelectionView, setIsOtpSelectionView,
    isEmailOtpView, setIsEmailOtpView,
    isSmsOtpView, setIsSmsOtpView,
    email, setEmail,
    smsPhone, setSmsPhone,
    otpSent, setOtpSent,
    smsOtpSent, setSmsOtpSent,
    emailOtp,
    smsOtp,
    otpIdentifier, setOtpIdentifier,
    isForgotPassword, setIsForgotPassword,
    forgotPasswordMethod, setForgotPasswordMethod,
    newPassword, setNewPassword,
    confirmPassword, setConfirmPassword,
    showNewPasswordForm
  };

  const authHandlers = {
    handleLogin,
    handleEmailLogin,
    handleSmsLogin,
    handleSendOtp,
    handleVerifyEmailOtp,
    handleVerifySmsOtp,
    resetPassword,
    resetToLogin,
    handleEmailOtpChange,
    handleSmsOtpChange,
    navigate
  };

  return (
    <PartnerLoginForm authStates={authStates} authHandlers={authHandlers} />
  );
};

export default LoginComponent;